<template>
    <div
        class="field-wrapper field-wrapper--floating-label field-wrapper--recurly"
        :class="classList">
        <div
            :id="inputConfig.recurlyId"
            :class="hostedFieldClassList"
            :data-recurly="inputConfig.dataRecurly"></div>

        <label :class="{ floated: focused || hasInput }">{{ inputConfig.label }}</label>

        <div v-if="inputConfig.iconClass" class="icon" :class="inputConfig.iconClass">
            <TooltipOverlay
                v-if="inputConfig.tooltipID"
                :id="inputConfig.tooltipID"
                :ariaLabel="$getLocale('cvv_info')"
                tabindex="0"
                :tooltipId="'tooltip-' + inputConfig.tooltipID">
                <div v-if="inputConfig.tooltipID === 'info-cvv'" aria-live="polite">
                    <h3>{{ $getLocale('cvv_info') }}</h3>
                    <p>
                        {{ $getLocale('the_location_varies_depending_on_credit_card') }}

                        <span class="sr-only">
                            {{ $getLocale('the_location_varies_depending_on_credit_card_sr_only') }}
                        </span>
                    </p>

                    <h4 aria-hidden="true">{{ $getLocale('3_digit_security_code') }}</h4>
                    <div aria-hidden="true" class="card-cvv card-cvv--three"></div>

                    <h4 aria-hidden="true">{{ $getLocale('4_digit_security_code') }}</h4>
                    <div aria-hidden="true" class="card-cvv card-cvv--four"></div>
                </div>
            </TooltipOverlay>
        </div>
        <!-- BEGIN PLOCTOPLUS-2033 -->
        <div v-if="shouldDisplayGreenLockIcon" class="cc-lock-icon"></div>
        <div
            v-else-if="shouldDisplayCheckmarkLockIcon"
            id="cc-lock-icon"
            class="cc-checkmark-lock-icon"
            @click="executeLockIconTracking()"></div>
        <!-- END PLOCTOPLUS-2033 -->
    </div>
</template>

<script>
    import TooltipOverlay from 'atoms/TooltipOverlay';
    import { tracking } from '../services/Tracking';

    const VALUE_TYPE = {
        EMPTY: 'EMPTY',
        VALID: 'VALID',
        INVALID: 'INVALID',
    };

    export default {
        components: {
            TooltipOverlay,
        },

        props: {
            inputConfig: {
                type: Object,
            },
            errorMessage: {
                type: String,
                required: false,
            },
        },

        data: function () {
            return {
                valueType: VALUE_TYPE.EMPTY,
                focused: false,
                wrapperObserver: null,
                hostedFieldObserver: null,
            };
        },

        computed: {
            // BEGIN PLOCTOPLUS-2033
            shouldDisplayCheckmarkLockIcon() {
                return (
                    this.inputConfig.recurlyId === 'cc-number' &&
                    this.$store.getters['variant/isPaymentHypertextLink']
                );
            },
            shouldDisplayGreenLockIcon() {
                return (
                    this.inputConfig.recurlyId === 'cc-number' &&
                    this.$store.getters['variant/isPaymentGreenLockIcon']
                );
            },
            // END PLOCTOPLUS-2033
            hasInput() {
                return this.valueType !== VALUE_TYPE.EMPTY;
            },

            classList() {
                return {
                    ['field-wrapper--' + this.inputConfig.recurlyId]: true,
                    error: !!this.errorMessage,
                };
            },

            hostedFieldClassList() {
                let styleClass = Array.isArray(this.inputConfig.styleClass)
                    ? this.inputConfig.styleClass
                    : [];

                return ['recurly-hosted-field-wrapper', ...styleClass];
            },
        },

        methods: {
            // START AB TEST PLOCTOPLUS-2033
            executeLockIconTracking() {
                tracking.trackAction('trackPaymentLockIconSelect');
            },
            // END AB TEST PLOCTOPLUS-2033
            setValueType(valueType, doEmit) {
                let didChange = valueType !== this.valueType;

                this.valueType = valueType;

                if (didChange || doEmit) {
                    this.$emit('input', {
                        value: this.hasInput ? this.valueType : '',
                    });
                }
            },

            setFocused(focused) {
                if (this.focused !== focused) {
                    this.focused = focused;

                    if (!this.focused) {
                        this.$emit('blur');
                    } else {
                        // START AB TEST PLOCTOPLUS-2033
                        this.$emit('focus');
                        // END AB TEST PLOCTOPLUS-2033
                    }
                }
            },
        },

        // Lifecycle Hooks:

        mounted: function () {
            let self = this;

            this.$nextTick(() => {
                let recurlyId = this.inputConfig.recurlyId;
                let wrapperNode = document.getElementById(recurlyId);

                if (wrapperNode) {
                    if (!self.hostedFieldObserver) {
                        self.hostedFieldObserver = new MutationObserver(
                            (mutationsList, observer) => {
                                for (var mutation of mutationsList) {
                                    if (mutation.type === 'attributes') {
                                        let field = mutation.target;

                                        if (
                                            field.classList.contains('recurly-hosted-field-valid')
                                        ) {
                                            self.setValueType(VALUE_TYPE.VALID);
                                        } else if (
                                            field.classList.contains('recurly-hosted-field-invalid')
                                        ) {
                                            self.setValueType(VALUE_TYPE.INVALID);
                                        } else {
                                            self.setValueType(VALUE_TYPE.EMPTY);
                                        }

                                        this.setFocused(
                                            field.classList.contains('recurly-hosted-field-focus'),
                                        );
                                    }
                                }
                            },
                        );
                    }

                    if (!self.wrapperObserver) {
                        self.wrapperObserver = new MutationObserver((mutationsList, observer) => {
                            for (var mutation of mutationsList) {
                                if (mutation.type === 'childList') {
                                    let field = document.querySelector(
                                        '#' + recurlyId + ' .recurly-hosted-field',
                                    );

                                    self.hostedFieldObserver.observe(field, { attributes: true });
                                }
                            }
                        });
                    }
                    self.wrapperObserver.observe(wrapperNode, { childList: true });
                }
            });
        },

        created: function () {
            this.setValueType(VALUE_TYPE.EMPTY, true);
        },

        beforeDestroy: function () {
            if (this.hostedFieldObserver) {
                this.hostedFieldObserver.disconnect();
            }

            if (this.wrapperObserver) {
                this.wrapperObserver.disconnect();
            }
        },
    };
</script>
